import { Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useAppSelector, useAppDispatch } from '@/store';
import { setSidebarOpen } from '@/store/app-slice';
import Header from './header';
import SideNav from './side-nav';
import { cn } from '@/lib/utils';

const SIDEBAR_WIDTH = 280; // px
const MOBILE_BREAKPOINT = 768; // px

export default function AppLayout() {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const sidebarOpen = useAppSelector(state => state.app.sidebarOpen);
  const dispatch = useAppDispatch();
  const [isMobile, setIsMobile] = useState(
    window.innerWidth < MOBILE_BREAKPOINT
  );

  // Debug logging

  // Handle responsive behavior
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < MOBILE_BREAKPOINT;
      const wasDesktop = !isMobile;
      const isNowMobile = mobile;

      setIsMobile(mobile);

      // Close sidebar when switching from desktop to mobile
      if (wasDesktop && isNowMobile && sidebarOpen) {
        dispatch(setSidebarOpen(false));
      }
      // Open sidebar when switching from mobile to desktop (if not already open)
      else if (!wasDesktop && !isNowMobile && !sidebarOpen) {
        dispatch(setSidebarOpen(true));
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [dispatch, sidebarOpen, isMobile]);

  // Initialize sidebar state based on screen size - ONLY on first load
  useEffect(() => {
    const hasInitialized = sessionStorage.getItem('sidebarInitialized');
    if (!hasInitialized) {
      // Desktop: open by default, Mobile: closed by default
      dispatch(setSidebarOpen(!isMobile));
      sessionStorage.setItem('sidebarInitialized', 'true');
    }
  }, [isMobile, dispatch]);

  const handleOverlayClick = () => {
    if (isMobile) {
      dispatch(setSidebarOpen(false));
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Mobile Overlay - Behind sidebar */}
      {isMobile && sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm md:hidden"
          onClick={handleOverlayClick}
        />
      )}

      {/* Sidebar - Modal style on mobile, fixed on desktop */}
      <aside
        className={cn(
          // Base styles
          'bg-sidebar border-sidebar-border transition-transform duration-300 ease-in-out',
          // Mobile: absolute modal style (md and down)
          'md:fixed md:inset-y-0 md:z-30',
          // Mobile: full screen modal
          'fixed inset-y-0 z-50',
          // Positioning
          isRTL ? 'right-0' : 'left-0',
          // Border
          isRTL ? 'border-l' : 'border-r',
          // Width - responsive
          'w-[280px] md:w-[280px]'
        )}
        style={{
          transform: sidebarOpen
            ? 'translateX(0)'
            : isRTL
              ? 'translateX(100%)'
              : 'translateX(-100%)',
        }}
      >
        <SideNav />
      </aside>

      {/* Main Content Container */}
      <div
        className={cn(
          'flex flex-1 flex-col min-w-0',
          'transition-all duration-300 ease-in-out',
          // Desktop: margin when sidebar is open
          // Mobile: no margin (sidebar is modal overlay)
          !isMobile && sidebarOpen && !isRTL && 'md:ml-[280px]',
          !isMobile && sidebarOpen && isRTL && 'md:mr-[280px]'
        )}
        style={{
          // Only apply margins on desktop (md+)
          marginLeft:
            !isMobile && sidebarOpen && !isRTL ? `${SIDEBAR_WIDTH}px` : 0,
          marginRight:
            !isMobile && sidebarOpen && isRTL ? `${SIDEBAR_WIDTH}px` : 0,
        }}
      >
        <Header />

        {/* Main Content */}
        <main
          className={cn(
            'flex-1 overflow-y-auto',
            'p-4 sm:p-6',
            'bg-background'
          )}
        >
          <div
            className="mx-auto max-w-screen-2xl w-full"
            dir={isRTL ? 'rtl' : 'ltr'}
          >
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
